import produce from 'immer';
import set from 'lodash/set';

import { debugEntities as debug } from 'Lib/debug';
import type { GlobalStateType } from 'Lib/reduxType';
import { pick } from 'Lib/utils/lodashUtils';

import type { EntitiesState } from './types';

/**
 * Generates an action payload for updating a single entity of a particular type
 *
 * @param  {string} type - entity type
 * @param  {string} id - ID of the entity to update
 * @param  {Object} updates - updated entity payload. This is intended to be a patch, but whether
 *         the full entity data is required depends on that entity's merge strategy
 * @return {Object} action payload containing the entity updates
 */
export function createEntityPayload(type: string, id: string, updates: any = {}) {
  return {
    entities: {
      [type]: { [id]: updates },
    },
  };
}

export type MergeStrategy = (
  previousEntity: Partial<EntitiesState>,
  updatedEntity: Partial<EntitiesState>
) => EntitiesState;
export const applyUpdatesToEntity = (
  previousEntity: Partial<EntitiesState> = {},
  updatedEntity: Partial<EntitiesState>,
  mergeStrategy?: MergeStrategy | null
) => {
  if (typeof mergeStrategy === 'function') {
    return mergeStrategy(previousEntity, updatedEntity);
  }
  return updatedEntity;
};

/**
 * Filters out entities that aren't on a allowlist of IDs for that entity type
 *
 * @param  {EntitiesState} entities - entities by type
 * @param  {Object} idsByType - mapping of entity types to IDs
 * @param  {boolean} [listedTypesOnly] - omits entity types that aren't keys of `idsByType` when set
 * @return {EntitiesState} filtered entities by type
 */
export const filterEntities = (
  entities: EntitiesState,
  idsByType: Record<string, any>,
  listedTypesOnly: boolean = false
) => {
  const unfilteredEntities = listedTypesOnly ? pick(entities, Object.keys(idsByType)) : entities;

  return produce(unfilteredEntities, (draft) => {
    Object.entries(idsByType).forEach(([type, value]) => {
      if (value === false) {
        // Delete all entities of this type
        draft[type] = {};
      } else if (value !== true) {
        // Filter by a list of IDs
        const ids: string[] = [].concat(value);
        draft[type] = pick(draft[type] as EntitiesState, ids);
      }
      // Otherwise, we explicitly want to keep all entities of this type
    });
  });
};

/**
 * Applies selective updates to the entities
 *
 * @param  {EntitiesState} entities - entities by type
 * @param  {Object} updatedEntities - entity updates
 * @param  {Object} [mergeStrategies] - mapping of entity types to merge strategies
 * @return {EntitiesState} filtered entities by type
 */
export const updateEntities = (
  entities: EntitiesState,
  updatedEntities: Partial<EntitiesState> = {},
  mergeStrategies: Record<string, MergeStrategy> = {}
): EntitiesState =>
  produce(entities, (draft) => {
    // Iterate through each entity type in the updates
    Object.entries(updatedEntities).forEach(([type, updatedEntitiesForType]) => {
      if (!draft[type]) {
        debug(`Encountered entity of type "${type}" was not previously declared`);
        draft[type] = {};
      }

      // Iterate through each individual entity in the updates for the current type
      const mergeStrategy = mergeStrategies[type];
      Object.entries(updatedEntitiesForType).forEach(([id, updatedEntity]) => {
        if (updatedEntity === false) {
          // Delete the entity entirely
          delete draft[type]?.[id];
        } else {
          // Update the entity using the merge strategy
          const previousEntity = draft[type]?.[id];
          const entity = applyUpdatesToEntity(previousEntity, updatedEntity, mergeStrategy);
          set(draft, [type, id], entity);
        }
      });
    });
  });

export const getIds = (state: GlobalStateType, ids: string[]) => ids;
export const getId = (state: GlobalStateType, id?: string) => id;
